<template>
  <div class="p-4">
    <!-- Header -->
    <div class="mb-4 flex justify-between items-center">
      <div>
        <div class="flex items-center gap-2 mb-1">
          <button @click="goBack" class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
            </svg>
          </button>
          <h1 class="text-xl font-bold text-gray-900 dark:text-white">
            {{ isEdit ? 'Editar Prototipo' : 'Nuevo Prototipo' }}
          </h1>
        </div>
        <p class="text-sm text-gray-600 dark:text-gray-400">
          {{ isEdit ? 'Modifica la información y configuración del prototipo' : 'Crea un nuevo prototipo con sus secciones y componentes' }}
        </p>
      </div>
      <div class="flex gap-2">
        <button @click="goBack" class="px-3 py-1.5 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors text-sm">
          Descartar
        </button>
        <button @click="saveDraft" :disabled="loading" class="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm">
          Guardar Borrador
        </button>
        <button @click="publishPrototipo" :disabled="loading" class="px-3 py-1.5 bg-violet-600 text-white rounded-md hover:bg-violet-700 disabled:opacity-50 transition-colors text-sm">
          {{ isEdit ? 'Actualizar' : 'Publicar' }} Prototipo
        </button>
      </div>
    </div>

    <div class="grid grid-cols-1 xl:grid-cols-3 gap-4">
      <!-- Información del Prototipo -->
      <div class="xl:col-span-2">
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-4">
          <h2 class="text-base font-semibold text-gray-900 dark:text-white mb-3">Información del Prototipo</h2>
          
          <div class="space-y-3">
            <div>
              <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre *</label>
              <input 
                v-model="prototipoForm.nombre" 
                type="text" 
                required 
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white text-sm" 
                placeholder="Nombre del prototipo"
                :class="{ 'border-red-500': validationErrors.nombre }"
              />
              <p v-if="validationErrors.nombre" class="text-red-500 text-xs mt-1">{{ validationErrors.nombre[0] }}</p>
            </div>

            <!-- Campos de Proyecto -->
            <div class="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Proyecto (5 dígitos)</label>
                <input
                  v-model="prototipoForm.proyecto"
                  type="text"
                  maxlength="5"
                  pattern="[0-9]{5}"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white text-sm"
                  placeholder="12345"
                  :class="{ 'border-red-500': validationErrors.proyecto }"
                  @input="updateFolio"
                />
                <p v-if="validationErrors.proyecto" class="text-red-500 text-xs mt-1">{{ validationErrors.proyecto[0] }}</p>
              </div>
              <div>
                <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Fracción (2 letras)</label>
                <input
                  v-model="prototipoForm.fraccion"
                  type="text"
                  maxlength="2"
                  pattern="[A-Za-z]{2}"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white text-sm"
                  placeholder="AB"
                  :class="{ 'border-red-500': validationErrors.fraccion }"
                  @input="updateFolio"
                />
                <p v-if="validationErrors.fraccion" class="text-red-500 text-xs mt-1">{{ validationErrors.fraccion[0] }}</p>
              </div>
              <div>
                <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Cliente (10 chars)</label>
                <input
                  v-model="prototipoForm.cliente"
                  type="text"
                  maxlength="10"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white text-sm"
                  placeholder="CLIENTE123"
                  :class="{ 'border-red-500': validationErrors.cliente }"
                  @input="updateFolio"
                />
                <p v-if="validationErrors.cliente" class="text-red-500 text-xs mt-1">{{ validationErrors.cliente[0] }}</p>
              </div>
              <div>
                <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Folio (Auto-generado)</label>
                <input
                  v-model="prototipoForm.folio"
                  type="text"
                  readonly
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-400 text-sm"
                  placeholder="Generado automáticamente"
                />
              </div>
            </div>

            <!-- Dimensiones -->
            <div class="grid grid-cols-3 gap-3">
              <div>
                <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Alto (cm) *</label>
                <input
                  v-model="prototipoForm.alto"
                  @input="calcularFormulas"
                  type="number"
                  step="0.01"
                  required
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white text-sm"
                  :class="{ 'border-red-500': validationErrors.alto }"
                />
                <p v-if="validationErrors.alto" class="text-red-500 text-xs mt-1">{{ validationErrors.alto[0] }}</p>
              </div>
              <div>
                <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Ancho (cm) *</label>
                <input
                  v-model="prototipoForm.ancho"
                  @input="calcularFormulas"
                  type="number"
                  step="0.01"
                  required
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white text-sm"
                  :class="{ 'border-red-500': validationErrors.ancho }"
                />
                <p v-if="validationErrors.ancho" class="text-red-500 text-xs mt-1">{{ validationErrors.ancho[0] }}</p>
              </div>
              <div>
                <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Profundidad (cm) *</label>
                <input
                  v-model="prototipoForm.profundidad"
                  type="number"
                  step="0.01"
                  required
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white text-sm"
                  :class="{ 'border-red-500': validationErrors.profundidad }"
                />
                <p v-if="validationErrors.profundidad" class="text-red-500 text-xs mt-1">{{ validationErrors.profundidad[0] }}</p>
              </div>
            </div>

            <!-- Campos de Fórmula -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
              <div>
                <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Poses *</label>
                <input
                  v-model.number="prototipoForm.poses"
                  @input="calcularFormulas"
                  type="number"
                  min="1"
                  required
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white text-sm"
                  placeholder="Ej: 4"
                />
              </div>

              <div>
                <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Pago Herramental</label>
                <select
                  v-model="prototipoForm.pago_herramental"
                  @change="calcularFormulas"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white text-sm"
                >
                  <option value="SI">SI</option>
                  <option value="NO">NO</option>
                </select>
              </div>

              <div>
                <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Cantidad</label>
                <input
                  v-model.number="prototipoForm.cantidad"
                  @input="calcularFormulas"
                  type="number"
                  min="1"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white text-sm"
                  placeholder="Ej: 1000"
                />
              </div>
            </div>

            <!-- Alerta para componentes POR_MEDIDA sin área -->
            <div 
              v-if="tieneComponentesPorMedidaSinArea" 
              class="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md"
            >
              <svg class="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 18.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div class="flex-1">
                <p class="text-sm font-medium text-amber-800 dark:text-amber-200">
                  Dimensiones requeridas
                </p>
                <p class="text-xs text-amber-700 dark:text-amber-300 mt-1">
                  Has seleccionado componentes que se cobran por área. Ingresa las dimensiones (alto y ancho) para calcular el costo correcto. Actualmente estos componentes muestran $0.00.
                </p>
              </div>
            </div>

            <!-- Debug de Fórmulas -->
            <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <h3 class="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-3">📊 Debug de Fórmulas</h3>

              <!-- Fórmula 1: Valor Pago Herramental -->
              <div class="mb-4">
                <h4 class="text-xs font-medium text-blue-700 dark:text-blue-300 mb-2">1️⃣ Valor Pago Herramental</h4>
                <div class="bg-white dark:bg-gray-800 rounded p-3 text-xs">
                  <div class="mb-2">
                    <strong>Condición:</strong> Pago Herramental = {{ formulasDebug.pagoHerramental }}
                  </div>

                  <div class="mb-2">
                    <strong>Componentes que afectan:</strong>
                    <div v-if="formulasDebug.componentesHerramental.length === 0" class="text-gray-500 italic ml-2">
                      (Ningún componente seleccionado)
                    </div>
                    <div v-else class="ml-2 space-y-1">
                      <div v-for="comp in formulasDebug.componentesHerramental" :key="comp.nombre" class="flex justify-between">
                        <span>{{ comp.nombre }} ({{ comp.tipo }})</span>
                        <span class="font-mono">${{ parseFloat(comp.costo_total || 0).toFixed(2) }}</span>
                      </div>
                      <div class="border-t pt-1 flex justify-between font-semibold">
                        <span>Suma Total:</span>
                        <span class="font-mono">${{ formulasDebug.sumaHerramental.toFixed(2) }}</span>
                      </div>
                    </div>
                  </div>

                  <div class="mb-2">
                    <strong>Fórmula:</strong>
                    <div class="bg-gray-100 dark:bg-gray-700 p-2 rounded font-mono text-xs">
                      <div v-if="formulasDebug.pagoHerramental === 'SI'">
                        Valor = Suma Herramental = ${{ formulasDebug.sumaHerramental.toFixed(2) }}
                      </div>
                      <div v-else>
                        Valor = Suma Herramental = ${{ formulasDebug.sumaHerramental.toFixed(2) }}
                        <br><small class="text-gray-500">(En prototipos no se divide por cantidad)</small>
                      </div>
                    </div>
                  </div>

                  <div class="text-green-600 dark:text-green-400 font-semibold">
                    <strong>Resultado:</strong> ${{ formulasDebug.valorPagoHerramental.toFixed(2) }}
                  </div>
                </div>
              </div>

              <!-- Fórmula 2: Costo Calculado -->
              <div>
                <h4 class="text-xs font-medium text-blue-700 dark:text-blue-300 mb-2">2️⃣ Costo Calculado</h4>
                <div class="bg-white dark:bg-gray-800 rounded p-3 text-xs">

                  <!-- Componentes Suma Base -->
                  <div class="mb-2">
                    <strong>Suma Base (÷ Poses):</strong>
                    <div v-if="formulasDebug.componentesSumaBase.length === 0" class="text-gray-500 italic ml-2">
                      (Ningún componente)
                    </div>
                    <div v-else class="ml-2 space-y-1">
                      <div v-for="comp in formulasDebug.componentesSumaBase" :key="comp.nombre" class="flex justify-between">
                        <span>{{ comp.nombre }}</span>
                        <span class="font-mono">${{ parseFloat(comp.costo_total || 0).toFixed(2) }}</span>
                      </div>
                      <div class="border-t pt-1 flex justify-between">
                        <span>Subtotal:</span>
                        <span class="font-mono">${{ formulasDebug.sumaSumaBase.toFixed(2) }}</span>
                      </div>
                      <div class="flex justify-between">
                        <span>÷ Poses ({{ formulasDebug.poses }}):</span>
                        <span class="font-mono">${{ formulasDebug.costoSumaBase.toFixed(2) }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Componentes Suma Adicional -->
                  <div class="mb-2">
                    <strong>Suma Adicional:</strong>
                    <div v-if="formulasDebug.componentesSumaAdicional.length === 0" class="text-gray-500 italic ml-2">
                      (Ningún componente)
                    </div>
                    <div v-else class="ml-2 space-y-1">
                      <div v-for="comp in formulasDebug.componentesSumaAdicional" :key="comp.nombre" class="flex justify-between">
                        <span>{{ comp.nombre }}</span>
                        <span class="font-mono">${{ parseFloat(comp.costo_total || 0).toFixed(2) }}</span>
                      </div>
                      <div class="border-t pt-1 flex justify-between font-semibold">
                        <span>Subtotal:</span>
                        <span class="font-mono">${{ formulasDebug.sumaSumaAdicional.toFixed(2) }}</span>
                      </div>
                    </div>
                  </div>

                  <div class="mb-2">
                    <strong>Fórmula Final:</strong>
                    <div class="bg-gray-100 dark:bg-gray-700 p-2 rounded font-mono text-xs">
                      Costo = (Suma Base ÷ Poses) + Suma Adicional<br>
                      Costo = (${{ formulasDebug.sumaSumaBase.toFixed(2) }} ÷ {{ formulasDebug.poses }}) + ${{ formulasDebug.sumaSumaAdicional.toFixed(2) }}<br>
                      Costo = ${{ formulasDebug.costoSumaBase.toFixed(2) }} + ${{ formulasDebug.sumaSumaAdicional.toFixed(2) }}
                    </div>
                  </div>

                  <div class="text-green-600 dark:text-green-400 font-semibold">
                    <strong>Resultado:</strong> ${{ formulasDebug.costoCalculado.toFixed(2) }}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Descripción</label>
              <div class="border border-gray-300 dark:border-gray-600 rounded-md">
                <!-- Toolbar simple -->
                <div class="flex items-center gap-1 p-2 border-b border-gray-200 dark:border-gray-600">
                  <button type="button" @click="formatText('bold')" class="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-600">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 4h8a4 4 0 014 4 4 4 0 01-4 4H6z M6 12h9a4 4 0 014 4 4 4 0 01-4 4H6z"/>
                    </svg>
                  </button>
                  <button type="button" @click="formatText('italic')" class="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-600">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 4v16m8-16v16"/>
                    </svg>
                  </button>
                </div>
                <textarea 
                  ref="descriptionTextarea"
                  v-model="prototipoForm.descripcion" 
                  rows="4" 
                  class="w-full px-3 py-2 border-0 focus:outline-none focus:ring-0 dark:bg-gray-700 dark:text-white text-sm resize-none" 
                  placeholder="Descripción detallada del prototipo"
                ></textarea>
              </div>
            </div>
          </div>
        </div>

        <!-- Secciones del Prototipo -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div class="flex justify-between items-center mb-3">
            <h2 class="text-base font-semibold text-gray-900 dark:text-white">Secciones del Prototipo</h2>
            <button @click="showAddSeccionModal = true" class="px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm flex items-center gap-1">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
              </svg>
              Agregar Sección
            </button>
          </div>

          <div class="space-y-3">
            <div v-for="(prototipoSeccion, seccionIndex) in prototipoSecciones" :key="prototipoSeccion.id || `new-${seccionIndex}`" class="border border-gray-200 dark:border-gray-600 rounded-lg p-3">
              <div class="flex justify-between items-center mb-2">
                <div class="flex items-center gap-2">
                  <h3 class="text-sm font-medium text-gray-900 dark:text-white">{{ prototipoSeccion.seccion?.nombre }}</h3>
                  <span class="text-xs text-gray-500 dark:text-gray-400">Orden: {{ prototipoSeccion.orden }}</span>
                </div>
                <div class="flex gap-1">
                  <button @click="moveSeccionUp(seccionIndex)" :disabled="seccionIndex === 0" class="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"/>
                    </svg>
                  </button>
                  <button @click="moveSeccionDown(seccionIndex)" :disabled="seccionIndex === prototipoSecciones.length - 1" class="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                    </svg>
                  </button>
                  <button @click="removeSeccion(seccionIndex)" class="p-1 text-red-500 hover:text-red-700">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                  </button>
                </div>
              </div>

              <!-- Componentes de la sección -->
              <div class="space-y-2">
                <h4 class="text-xs font-medium text-gray-700 dark:text-gray-300">Tipos de componentes:</h4>
                <div v-for="(tipoComponente, compIndex) in prototipoSeccion.seccion?.tipos_componente || []" :key="`${tipoComponente.id}-${compIndex}`" class="flex items-center gap-3 p-2 bg-gray-50 dark:bg-gray-700 rounded">
                 <!-- <pre>
                  "posicion_formula": {{ tipoComponente.posicion_formula }}
                  "afecta_pago_herramental": {{ tipoComponente.afecta_pago_herramental }}
                  <br/>
                 </pre> -->
                  <div class="flex-1">
                    <div class="flex items-center gap-2">
                      <div class="text-xs font-medium text-gray-900 dark:text-white">{{ tipoComponente.nombre }}</div>
                      <span 
                        v-if="tipoComponente.tipo_costo === 'UNICO'" 
                        class="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                        title="Costo único - Se suma directamente"
                      >
                        Único
                      </span>
                      <span 
                        v-else-if="tipoComponente.tipo_costo === 'POR_MEDIDA'" 
                        class="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
                        title="Por medida - Se multiplica por área del producto"
                      >
                        x Área
                      </span>
                      <span 
                        v-else 
                        class="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
                      >
                        N/D
                      </span>
                    </div>
                    <div class="text-xs text-gray-500 dark:text-gray-400">{{ tipoComponente.alias || 'Orden: ' + tipoComponente.orden }}</div>
                  </div>
                  <div 
                    class="flex-1 relative"
                    :ref="el => dropdownRefs[getSearchKey(prototipoSeccion, tipoComponente)] = el"
                  >
                    <div class="relative">
                      <input
                        v-model="searchTerms[getSearchKey(prototipoSeccion, tipoComponente)]"
                        @input="handleInputChange(prototipoSeccion, tipoComponente)"
                        @focus="openDropdown(prototipoSeccion, tipoComponente)"
                        @keydown.escape="closeDropdown(prototipoSeccion, tipoComponente)"
                        @keydown.enter.prevent="selectHighlightedOrFirst(prototipoSeccion, tipoComponente)"
                        @keydown.arrow-down.prevent="navigateDown(prototipoSeccion, tipoComponente)"
                        @keydown.arrow-up.prevent="navigateUp(prototipoSeccion, tipoComponente)"
                        :placeholder="`${getSelectedComponentePlaceholder(prototipoSeccion, tipoComponente) || 'Buscar ' + tipoComponente.nombre.toLowerCase() + '...'}`"
                        :title="getSelectedComponenteFullName(prototipoSeccion, tipoComponente)"
                        class="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs focus:outline-none focus:ring-1 focus:ring-violet-500 dark:bg-gray-600 dark:text-white"
                      />
                    </div>
                    
                    <!-- Dropdown con resultados filtrados -->
                    <div 
                      v-if="showDropdown[getSearchKey(prototipoSeccion, tipoComponente)]" 
                      class="absolute z-50 w-full max-h-40 overflow-y-auto bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg mt-1"
                    >
                      <!-- Opción "No Aplica" siempre visible -->
                      <div
                        @click="selectNoAplica(prototipoSeccion, tipoComponente)"
                        @mouseover="highlightedIndex[getSearchKey(prototipoSeccion, tipoComponente)] = -1"
                        :data-highlighted="highlightedIndex[getSearchKey(prototipoSeccion, tipoComponente)] === -1 ? 'true' : 'false'"
                        :class="[
                          'px-3 py-2 cursor-pointer text-xs border-b border-gray-200 dark:border-gray-600 text-orange-600 dark:text-orange-400 font-medium',
                          highlightedIndex[getSearchKey(prototipoSeccion, tipoComponente)] === -1
                            ? 'bg-violet-100 dark:bg-violet-800'
                            : 'hover:bg-gray-100 dark:hover:bg-gray-600'
                        ]"
                      >
                        -- No Aplica -- - $0
                      </div>
                      
                      <!-- Componentes filtrados -->
                      <div
                        v-for="(componente, index) in filteredComponents[getSearchKey(prototipoSeccion, tipoComponente)]"
                        :key="componente.id"
                        @click="selectComponent(prototipoSeccion, tipoComponente, componente)"
                        @mouseover="highlightedIndex[getSearchKey(prototipoSeccion, tipoComponente)] = index"
                        :data-highlighted="highlightedIndex[getSearchKey(prototipoSeccion, tipoComponente)] === index ? 'true' : 'false'"
                        :title="`${componente.nombre} - $${componente.costo_unitario || 0} ${tipoComponente.tipo_costo === 'POR_MEDIDA' ? '(Por área)' : '(Costo único)'}`"
                        :class="[
                          'px-3 py-2 cursor-pointer text-xs',
                          highlightedIndex[getSearchKey(prototipoSeccion, tipoComponente)] === index 
                            ? 'bg-violet-100 dark:bg-violet-800' 
                            : 'hover:bg-gray-100 dark:hover:bg-gray-600'
                        ]"
                      >
                        <div class="flex justify-between items-center">
                          <div class="flex-1">
                            <div class="font-medium text-gray-900 dark:text-white truncate">
                              {{ componente.nombre }}
                            </div>
                            <div class="text-gray-500 dark:text-gray-400">${{ componente.costo_unitario || 0 }}</div>
                          </div>
                        </div>
                      </div>
                      
                      <!-- Sin resultados -->
                      <div 
                        v-if="!filteredComponents[getSearchKey(prototipoSeccion, tipoComponente)]?.length"
                        class="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 italic"
                      >
                        No se encontraron componentes
                      </div>
                    </div>
                  </div>
                  <div class="text-xs text-green-600 dark:text-green-400 font-medium min-w-[60px]">
                    ${{ getComponenteCosto(prototipoSeccion, tipoComponente) }}
                  </div>
                </div>
              </div>
            </div>

            <div v-if="prototipoSecciones.length === 0" class="text-center py-6 text-gray-400 text-sm">
              No hay secciones agregadas. Haz clic en "Agregar Sección" para comenzar.
            </div>
          </div>
        </div>
      </div>

      <!-- Panel Lateral -->
      <div class="space-y-4">
        <!-- Precios Calculados -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <h2 class="text-base font-semibold text-gray-900 dark:text-white mb-3">
            Precios de Venta
            <span v-if="userStore.negocioSeleccionado" class="text-xs font-normal text-gray-500 dark:text-gray-400 ml-2">
              ({{ userStore.negocioSeleccionado.nombre }})
            </span>
          </h2>

          <!-- Información del negocio y porcentajes -->
          <div v-if="userStore.negocioSeleccionado" class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4">
            <h3 class="text-sm font-medium text-blue-900 dark:text-blue-200 mb-2">Porcentajes de Ganancia Configurados</h3>
            <div class="grid grid-cols-2 gap-2">
              <div class="flex justify-between">
                <span class="text-xs text-blue-700 dark:text-blue-300">Precio 1:</span>
                <span class="text-xs font-medium text-blue-800 dark:text-blue-200">{{ userStore.negocioSeleccionado.porcentaje1 || 25.00 }}%</span>
              </div>
              <div class="flex justify-between">
                <span class="text-xs text-blue-700 dark:text-blue-300">Precio 2:</span>
                <span class="text-xs font-medium text-blue-800 dark:text-blue-200">{{ userStore.negocioSeleccionado.porcentaje2 || 35.00 }}%</span>
              </div>
              <div class="flex justify-between">
                <span class="text-xs text-blue-700 dark:text-blue-300">Precio 3:</span>
                <span class="text-xs font-medium text-blue-800 dark:text-blue-200">{{ userStore.negocioSeleccionado.porcentaje3 || 50.00 }}%</span>
              </div>
              <div class="flex justify-between">
                <span class="text-xs text-blue-700 dark:text-blue-300">Precio 4:</span>
                <span class="text-xs font-medium text-blue-800 dark:text-blue-200">{{ userStore.negocioSeleccionado.porcentaje4 || 75.00 }}%</span>
              </div>
            </div>
          </div>

          <!-- Precios calculados automáticamente -->
          <div class="grid grid-cols-2 gap-4">
            <div class="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 p-3 rounded-lg border border-blue-200 dark:border-blue-700">
              <label class="block text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">Precio 1 (+{{ porcentajesNegocio.porcentaje1 }}%)</label>
              <div class="text-lg font-bold text-blue-800 dark:text-blue-200">
                ${{ preciosCalculados.precio1.toFixed(2) }}
              </div>
              <div class="text-xs text-blue-600 dark:text-blue-400 mt-1">
                Costo: ${{ costoBase.toFixed(2) }} + {{ porcentajesNegocio.porcentaje1 }}%
              </div>
            </div>

            <div class="bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30 p-3 rounded-lg border border-green-200 dark:border-green-700">
              <label class="block text-xs font-medium text-green-700 dark:text-green-300 mb-1">Precio 2 (+{{ porcentajesNegocio.porcentaje2 }}%)</label>
              <div class="text-lg font-bold text-green-800 dark:text-green-200">
                ${{ preciosCalculados.precio2.toFixed(2) }}
              </div>
              <div class="text-xs text-green-600 dark:text-green-400 mt-1">
                Costo: ${{ costoBase.toFixed(2) }} + {{ porcentajesNegocio.porcentaje2 }}%
              </div>
            </div>

            <div class="bg-gradient-to-r from-yellow-50 to-yellow-100 dark:from-yellow-900/30 dark:to-yellow-800/30 p-3 rounded-lg border border-yellow-200 dark:border-yellow-700">
              <label class="block text-xs font-medium text-yellow-700 dark:text-yellow-300 mb-1">Precio 3 (+{{ porcentajesNegocio.porcentaje3 }}%)</label>
              <div class="text-lg font-bold text-yellow-800 dark:text-yellow-200">
                ${{ preciosCalculados.precio3.toFixed(2) }}
              </div>
              <div class="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                Costo: ${{ costoBase.toFixed(2) }} + {{ porcentajesNegocio.porcentaje3 }}%
              </div>
            </div>

            <div class="bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/30 p-3 rounded-lg border border-purple-200 dark:border-purple-700">
              <label class="block text-xs font-medium text-purple-700 dark:text-purple-300 mb-1">Precio 4 (+{{ porcentajesNegocio.porcentaje4 }}%)</label>
              <div class="text-lg font-bold text-purple-800 dark:text-purple-200">
                ${{ preciosCalculados.precio4.toFixed(2) }}
              </div>
              <div class="text-xs text-purple-600 dark:text-purple-400 mt-1">
                Costo: ${{ costoBase.toFixed(2) }} + {{ porcentajesNegocio.porcentaje4 }}%
              </div>
            </div>
          </div>

          <!-- Precios de Referencia -->
          <div class="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
            <h3 class="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-3">
              Precios de Referencia (sin herramental)
            </h3>
            <div class="grid grid-cols-2 gap-3 text-xs">
              <div class="bg-white dark:bg-gray-800 p-2 rounded border">
                <div class="text-blue-700 dark:text-blue-300 font-medium">Precio 1 (+{{ porcentajesNegocio.porcentaje1 }}%)</div>
                <div class="text-lg font-bold text-blue-800 dark:text-blue-200">
                  ${{ preciosCalculados.precio1.toFixed(2) }}
                </div>
              </div>
              <div class="bg-white dark:bg-gray-800 p-2 rounded border">
                <div class="text-blue-700 dark:text-blue-300 font-medium">Precio 2 (+{{ porcentajesNegocio.porcentaje2 }}%)</div>
                <div class="text-lg font-bold text-blue-800 dark:text-blue-200">
                  ${{ preciosCalculados.precio2.toFixed(2) }}
                </div>
              </div>
              <div class="bg-white dark:bg-gray-800 p-2 rounded border">
                <div class="text-blue-700 dark:text-blue-300 font-medium">Precio 3 (+{{ porcentajesNegocio.porcentaje3 }}%)</div>
                <div class="text-lg font-bold text-blue-800 dark:text-blue-200">
                  ${{ preciosCalculados.precio3.toFixed(2) }}
                </div>
              </div>
              <div class="bg-white dark:bg-gray-800 p-2 rounded border">
                <div class="text-blue-700 dark:text-blue-300 font-medium">Precio 4 (+{{ porcentajesNegocio.porcentaje4 }}%)</div>
                <div class="text-lg font-bold text-blue-800 dark:text-blue-200">
                  ${{ preciosCalculados.precio4.toFixed(2) }}
                </div>
              </div>
            </div>
            <p class="text-xs text-blue-600 dark:text-blue-400 mt-3">
              <svg class="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <span v-if="prototipoForm.pago_herramental === 'NO'">
                <strong>Precios de referencia.</strong> En cotizaciones se recalcularán incluyendo herramental según cantidad específica.
              </span>
              <span v-else>
                <strong>Precios fijos.</strong> En cotizaciones solo se agregará el valor total del herramental (${{ (prototipoForm.valor_pago_herramental || 0).toFixed(2) }}) por separado.
              </span>
            </p>
          </div>

          <!-- Nota informativa -->
          <div class="mt-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
            <p class="text-xs text-gray-600 dark:text-gray-400">
              <svg class="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              Los precios se calculan automáticamente basándose en el costo total de componentes y los porcentajes configurados para tu negocio.
            </p>
          </div>
        </div>

        <!-- Resumen de Costos -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <h2 class="text-base font-semibold text-gray-900 dark:text-white mb-3">Resumen de Costos</h2>

          <div class="space-y-2 text-xs">
            <div class="flex justify-between" v-if="(parseFloat(prototipoForm.alto) || 0) > 0 && (parseFloat(prototipoForm.ancho) || 0) > 0">
              <span class="text-gray-600 dark:text-gray-400">Área:</span>
              <span class="font-medium text-gray-900 dark:text-white">{{ ((parseFloat(prototipoForm.alto) || 0) * (parseFloat(prototipoForm.ancho) || 0) / 10000).toFixed(2) }} m²</span>
            </div>
            <!-- COMENTADO: Costo anterior sin fórmulas de Excel
            <div class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Costo total de componentes:</span>
              <span class="font-medium text-gray-900 dark:text-white">${{ totalCostoComponentes.toFixed(2) }}</span>
            </div>
            -->
            <div class="flex justify-between border-t border-gray-200 dark:border-gray-600 pt-2">
              <span class="text-gray-600 dark:text-gray-400">Costo base del prototipo:</span>
              <span class="font-medium text-green-600 dark:text-green-400">${{ costoBase.toFixed(2) }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Valor Pago Herramental:</span>
              <span class="font-medium text-blue-600 dark:text-blue-400">${{ (prototipoForm.valor_pago_herramental || 0).toFixed(2) }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Costo Calculado (Fórmula):</span>
              <span class="font-medium text-purple-600 dark:text-purple-400">${{ (prototipoForm.costo_calculado || 0).toFixed(2) }}</span>
            </div>
            <div class="flex justify-between text-sm">
              <span class="text-gray-600 dark:text-gray-400">Rango de precios de venta:</span>
              <span class="font-medium text-blue-600 dark:text-blue-400">
                ${{ preciosCalculados.precio1.toFixed(2) }} - ${{ preciosCalculados.precio4.toFixed(2) }}
              </span>
            </div>
          </div>
        </div>

        
      </div>
    </div>

    <!-- Modal para Agregar Sección -->
    <div v-if="showAddSeccionModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
        <div class="p-4 border-b border-gray-200 dark:border-gray-700">
          <div class="flex justify-between items-center">
            <h3 class="text-base font-semibold text-gray-900 dark:text-white">Agregar Sección</h3>
            <button @click="showAddSeccionModal = false" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>
        <div class="p-4">
          <div class="space-y-3">
            <div v-for="seccion in seccionesDisponibles" :key="seccion.id" class="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
              <div>
                <div class="text-sm font-medium text-gray-900 dark:text-white">{{ seccion.nombre }}</div>
                <div class="text-xs text-gray-500 dark:text-gray-400">{{ seccion.tipos_componente?.length || 0 }} tipos de componentes</div>
              </div>
              <button 
                @click="addSeccion(seccion)"
                :disabled="isSeccionAlreadyAdded(seccion)"
                class="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {{ isSeccionAlreadyAdded(seccion) ? 'Ya agregada' : 'Agregar' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useHttp } from '@/composables/useHttp'
import { useNotifications } from '@/composables/useNotifications'
import { useUserStore } from '@/stores/user'
import Swal from 'sweetalert2'

const router = useRouter()
const route = useRoute()
const { get, post, del, loading, error, data } = useHttp()
const { showSuccess, showError } = useNotifications()
const userStore = useUserStore()

// Estado
const isEdit = computed(() => !!route.params.id)
const prototipoId = computed(() => route.params.id)

// Formulario principal
const prototipoForm = ref({
  id: null,
  nombre: '',
  descripcion: '',
  proyecto: '',
  fraccion: '',
  cliente: '',
  folio: '',
  pago_herramental: 'SI',
  poses: null,
  cantidad: 1,
  valor_pago_herramental: 0,
  costo_calculado: 0,
  alto: '',
  ancho: '',
  profundidad: '',
  aplicar_impuestos: true,
  en_stock: true,
  categoria_id: '',
  etiquetas: ''
})

// Datos auxiliares
const prototipoSecciones = ref([])
const prototipoComponentes = ref([])
const seccionesDisponibles = ref([])
const validationErrors = ref({})

// Modales
const showAddSeccionModal = ref(false)

// Estado para select con búsqueda
const searchTerms = ref({})
const showDropdown = ref({})
const filteredComponents = ref({})
const dropdownRefs = ref({})
const highlightedIndex = ref({})

// Referencias
const descriptionTextarea = ref(null)

// Computed
// CÓDIGO ANTERIOR - CÁLCULO DE COSTO BASE SIMPLE (COMENTADO)
// Este era el cálculo anterior que sumaba todos los componentes sin considerar las fórmulas de Excel
/*
const totalCostoComponentes = computed(() => {
  let total = 0
  const alto = parseFloat(prototipoForm.value.alto) || 0
  const ancho = parseFloat(prototipoForm.value.ancho) || 0
  const area = (alto * ancho) / 10000 // Convertir de cm² a m² (dividir entre 10000)

  prototipoComponentes.value.forEach(pc => {
    // Buscar el componente y su tipo de componente en todas las secciones
    let componente = null
    let tipoComponente = null

    for (const seccion of seccionesDisponibles.value) {
      for (const tipo of seccion.tipos_componente || []) {
        componente = tipo.componentes?.find(c => c.id === pc.componente_id)
        if (componente) {
          tipoComponente = tipo
          break
        }
      }
      if (componente) break
    }

    if (componente && tipoComponente) {
      const costoUnitario = parseFloat(componente.costo_unitario || 0)

      if (tipoComponente.tipo_costo === 'POR_MEDIDA'){ // && area > 0) {
        // Multiplicar por el área en metros cuadrados
        total += costoUnitario * area
      } else {
        // Costo único, se suma directamente
        total += costoUnitario
      }
    }
  })
  return total
})

// Costo base del prototipo (igual al costo total de componentes)
const costoBase = computed(() => {
  return totalCostoComponentes.value
})
*/

// Porcentajes del negocio seleccionado
const porcentajesNegocio = computed(() => {
  if (!userStore.negocioSeleccionado) {
    return {
      porcentaje1: 25.00,
      porcentaje2: 35.00,
      porcentaje3: 50.00,
      porcentaje4: 75.00
    }
  }

  return {
    porcentaje1: parseFloat(userStore.negocioSeleccionado.porcentaje1) || 25.00,
    porcentaje2: parseFloat(userStore.negocioSeleccionado.porcentaje2) || 35.00,
    porcentaje3: parseFloat(userStore.negocioSeleccionado.porcentaje3) || 50.00,
    porcentaje4: parseFloat(userStore.negocioSeleccionado.porcentaje4) || 75.00
  }
})

// Nuevo costo base basado en la fórmula de Excel
const costoBase = computed(() => {
  return prototipoForm.value.costo_calculado || 0
})

// NOTA: Los precios unitarios ahora se calculan dinámicamente en cotizaciones
// según la cantidad específica y si incluyen herramental o no

// Precios calculados basados en el nuevo costo base (fórmula) y los porcentajes
const preciosCalculados = computed(() => {
  const base = costoBase.value
  const porcentajes = porcentajesNegocio.value

  return {
    precio1: base * (1 + porcentajes.porcentaje1 / 100),
    precio2: base * (1 + porcentajes.porcentaje2 / 100),
    precio3: base * (1 + porcentajes.porcentaje3 / 100),
    precio4: base * (1 + porcentajes.porcentaje4 / 100)
  }
})

const tieneComponentesPorMedidaSinArea = computed(() => {
  const alto = parseFloat(prototipoForm.value.alto) || 0
  const ancho = parseFloat(prototipoForm.value.ancho) || 0
  const area = alto * ancho
  
  if (area > 0) return false
  
  // Verificar si hay componentes seleccionados que sean POR_MEDIDA
  return prototipoComponentes.value.some(pc => {
    // Buscar el tipo de componente
    for (const seccion of seccionesDisponibles.value) {
      for (const tipoComponente of seccion.tipos_componente || []) {
        const componente = tipoComponente.componentes?.find(c => c.id === pc.componente_id)
        if (componente && tipoComponente.tipo_costo === 'POR_MEDIDA') {
          return true
        }
      }
    }
    return false
  })
})

// Computed que obtiene todos los tipos de componente de todas las secciones
const tiposComponente = computed(() => {
  const tipos = []
  seccionesDisponibles.value.forEach(seccion => {
    if (seccion.tipos_componente) {
      tipos.push(...seccion.tipos_componente)
    }
  })
  return tipos
})

// Computed para mostrar la descomposición de las fórmulas
const formulasDebug = computed(() => {
  // 1. Componentes que afectan pago herramental
  const componentesHerramental = prototipoComponentes.value.filter(pc => {
    const tipoComponente = tiposComponente.value.find(tc => tc.id === pc.tipo_componente_id)
    return tipoComponente && tipoComponente.afecta_pago_herramental
  }).map(pc => {
    const tipoComponente = tiposComponente.value.find(tc => tc.id === pc.tipo_componente_id)
    const componente = tipoComponente?.componentes?.find(c => c.id === pc.componente_id)
    return {
      nombre: componente?.nombre || 'Sin nombre',
      tipo: tipoComponente?.nombre || 'Sin tipo',
      costo_unitario: pc.costo_unitario || 0,
      cantidad: pc.cantidad || 1,
      costo_total: pc.costo_total || 0,
      tipo_costo: tipoComponente?.tipo_costo || 'UNICO'
    }
  })

  // 2. Componentes suma base
  const componentesSumaBase = prototipoComponentes.value.filter(pc => {
    const tipoComponente = tiposComponente.value.find(tc => tc.id === pc.tipo_componente_id)
    return tipoComponente && tipoComponente.posicion_formula === 'suma_base'
  }).map(pc => {
    const tipoComponente = tiposComponente.value.find(tc => tc.id === pc.tipo_componente_id)
    const componente = tipoComponente?.componentes?.find(c => c.id === pc.componente_id)
    return {
      nombre: componente?.nombre || 'Sin nombre',
      tipo: tipoComponente?.nombre || 'Sin tipo',
      costo_unitario: pc.costo_unitario || 0,
      cantidad: pc.cantidad || 1,
      costo_total: pc.costo_total || 0,
      tipo_costo: tipoComponente?.tipo_costo || 'UNICO'
    }
  })

  // 3. Componentes suma adicional
  const componentesSumaAdicional = prototipoComponentes.value.filter(pc => {
    const tipoComponente = tiposComponente.value.find(tc => tc.id === pc.tipo_componente_id)
    return tipoComponente && tipoComponente.posicion_formula === 'suma_adicional'
  }).map(pc => {
    const tipoComponente = tiposComponente.value.find(tc => tc.id === pc.tipo_componente_id)
    const componente = tipoComponente?.componentes?.find(c => c.id === pc.componente_id)
    return {
      nombre: componente?.nombre || 'Sin nombre',
      tipo: tipoComponente?.nombre || 'Sin tipo',
      costo_unitario: pc.costo_unitario || 0,
      cantidad: pc.cantidad || 1,
      costo_total: pc.costo_total || 0,
      tipo_costo: tipoComponente?.tipo_costo || 'UNICO'
    }
  })

  // Cálculos
  const sumaHerramental = componentesHerramental.reduce((sum, pc) => sum + parseFloat(pc.costo_total || 0), 0)
  const sumaSumaBase = componentesSumaBase.reduce((sum, pc) => sum + parseFloat(pc.costo_total || 0), 0)
  const sumaSumaAdicional = componentesSumaAdicional.reduce((sum, pc) => sum + parseFloat(pc.costo_total || 0), 0)

  const poses = prototipoForm.value.poses || 1
  const costoSumaBase = poses > 0 ? sumaSumaBase / poses : 0

  return {
    componentesHerramental,
    componentesSumaBase,
    componentesSumaAdicional,
    sumaHerramental,
    sumaSumaBase,
    sumaSumaAdicional,
    poses,
    costoSumaBase,
    pagoHerramental: prototipoForm.value.pago_herramental,
    valorPagoHerramental: prototipoForm.value.valor_pago_herramental || 0,
    costoCalculado: prototipoForm.value.costo_calculado || 0
  }
})

// Función helper para calcular área con ROUNDUP (como Excel)
const calcularArea = (alto, ancho) => {
  const altoNum = parseFloat(alto) || 0
  const anchoNum = parseFloat(ancho) || 0
  const areaCm2 = altoNum * anchoNum
  const areaM2 = areaCm2 / 10000 // Convertir cm² a m²

  // Aplicar ROUNDUP a 2 decimales para coincidir con Excel
  // ROUNDUP siempre redondea hacia arriba
  return Math.ceil(areaM2 * 100) / 100
}

// Función para actualizar el folio automáticamente
const updateFolio = () => {
  const { proyecto, fraccion, cliente } = prototipoForm.value
  if (proyecto && fraccion && cliente) {
    prototipoForm.value.folio = cliente + '-' + proyecto + '-' + fraccion
  } else {
    prototipoForm.value.folio = ''
  }
}

// Función para calcular las fórmulas de pago herramental y costo
const calcularFormulas = () => {
  console.log('=== CALCULANDO FÓRMULAS ===')
  console.log('Tipos de componente disponibles:', tiposComponente.value.map(tc => ({
    id: tc.id,
    nombre: tc.nombre,
    posicion_formula: tc.posicion_formula,
    afecta_pago_herramental: tc.afecta_pago_herramental
  })))
  console.log('Componentes del prototipo:', prototipoComponentes.value.map(pc => ({
    id: pc.id,
    tipo_componente_id: pc.tipo_componente_id,
    costo_total: pc.costo_total
  })))

  // 1. Calcular Valor Pago Herramental
  console.log('Filtrando componentes herramental...')

  const componentesHerramental = prototipoComponentes.value.filter(pc => {
    const tipoComponente = tiposComponente.value.find(tc => tc.id === pc.tipo_componente_id)
    console.log(`Componente ID ${pc.tipo_componente_id}:`, {
      encontrado: !!tipoComponente,
      nombre: tipoComponente?.nombre,
      afecta_pago_herramental: tipoComponente?.afecta_pago_herramental,
      costo_total: pc.costo_total
    })
    return tipoComponente && tipoComponente.afecta_pago_herramental
  })

  console.log('Componentes que afectan pago herramental:', componentesHerramental)

  const sumaHerramental = componentesHerramental.reduce((sum, pc) => sum + (parseFloat(pc.costo_total) || 0), 0)
  console.log('Suma herramental:', sumaHerramental, 'Pago herramental:', prototipoForm.value.pago_herramental)

  if (prototipoForm.value.pago_herramental === 'SI') {
    prototipoForm.value.valor_pago_herramental = sumaHerramental
    console.log('Pago herramental SI - Valor asignado:', sumaHerramental)
  } else {
    // En prototipos no usamos cantidad, siempre será como cantidad = 1
    prototipoForm.value.valor_pago_herramental = sumaHerramental
    console.log('Pago herramental NO - Valor asignado directamente:', prototipoForm.value.valor_pago_herramental)
  }

  console.log('RESULTADO FINAL valor_pago_herramental:', prototipoForm.value.valor_pago_herramental)

  // 2. Calcular Costo (Fórmula principal)
  const componentesSumaBase = prototipoComponentes.value.filter(pc => {
    const tipoComponente = tiposComponente.value.find(tc => tc.id === pc.tipo_componente_id)
    return tipoComponente && tipoComponente.posicion_formula === 'suma_base'
  })

  const componentesSumaAdicional = prototipoComponentes.value.filter(pc => {
    const tipoComponente = tiposComponente.value.find(tc => tc.id === pc.tipo_componente_id)
    return tipoComponente && tipoComponente.posicion_formula === 'suma_adicional'
  })

  console.log('Componentes suma base:', componentesSumaBase)
  console.log('Componentes suma adicional:', componentesSumaAdicional)

  const sumaSumaBase = componentesSumaBase.reduce((sum, pc) => sum + (parseFloat(pc.costo_total) || 0), 0)
  const sumaSumaAdicional = componentesSumaAdicional.reduce((sum, pc) => sum + (parseFloat(pc.costo_total) || 0), 0)

  const poses = prototipoForm.value.poses || 1
  const costoSumaBase = poses > 0 ? sumaSumaBase / poses : 0

  console.log('Cálculo final:', {
    sumaSumaBase,
    poses,
    costoSumaBase,
    sumaSumaAdicional,
    total: costoSumaBase + sumaSumaAdicional
  })

  // El costo calculado solo incluye suma base + suma adicional (SIN herramental)
  prototipoForm.value.costo_calculado = costoSumaBase + sumaSumaAdicional
  console.log(`Costo base calculado: $${prototipoForm.value.costo_calculado.toFixed(2)} (SIN herramental)`);

  if (prototipoForm.value.pago_herramental === 'NO') {
    console.log(`Herramental ($${prototipoForm.value.valor_pago_herramental}) se calculará dinámicamente en cotizaciones según cantidad`);
  } else {
    console.log(`Herramental ($${prototipoForm.value.valor_pago_herramental}) se cobrará por separado en cotizaciones`);
  }
}

// Métodos principales
const loadInitialData = async () => {
  try {
    // Cargar secciones disponibles con sus tipos de componente y componentes
    await get('secciones/catalogo-completo')
    if (data.value) {
      // Si ya es un array, lo asignamos directamente
      seccionesDisponibles.value = Array.isArray(data.value) ? data.value : (data.value.data || data.value)
      console.log("secciones cargadas: ", seccionesDisponibles.value);
    }

    // Si es edición, cargar datos del prototipo
    if (isEdit.value) {
      await loadPrototipoData()
    }
  } catch (err) {
    showError('Error al cargar datos iniciales')
  }
}

const loadPrototipoData = async () => {
  try {
    await get(`prototipos/${prototipoId.value}`)
    if (data.value) {
      console.log('Datos del prototipo cargados:', data.value)


      // Cargar solo los campos necesarios, evitando sobrescribir campos calculados
      prototipoForm.value = {
        ...prototipoForm.value, // Mantener valores por defecto
        id: data.value.id,
        nombre: data.value.nombre,
        descripcion: data.value.descripcion,
        alto: data.value.alto,
        ancho: data.value.ancho,
        profundidad: data.value.profundidad,
        proyecto: data.value.proyecto,
        fraccion: data.value.fraccion,
        cliente: data.value.cliente,
        poses: data.value.poses,
        pago_herramental: data.value.pago_herramental,
        valor_pago_herramental: data.value.valor_pago_herramental,
        costo_calculado: data.value.costo_calculado,
        precio_unitario: data.value.precio_unitario,
        estado: data.value.estado,
        notas: data.value.notas
      }
      // Los datos se asignan correctamente al prototipoForm

      // Las secciones y componentes ya vienen cargados en la respuesta del show
      // Verificar ambos formatos por si acaso
      const secciones = data.value.prototipoSecciones || data.value.prototipo_secciones
      if (secciones) {
        prototipoSecciones.value = secciones
      }
      
      // Extraer todos los componentes de todas las secciones
      const todosComponentes = []
      const seccionesData = data.value.prototipoSecciones || data.value.prototipo_secciones
      if (seccionesData) {
        seccionesData.forEach(seccion => {
          // Verificar ambos formatos de componentes
          const componentes = seccion.prototipoComponentes || seccion.prototipo_componentes
          if (componentes) {
            componentes.forEach(componente => {
              todosComponentes.push(componente)
            })
          }
        })
      }
      prototipoComponentes.value = todosComponentes
      
      // Inicializar términos de búsqueda para componentes seleccionados
      initializeSearchTermsFromSelection()

      // Calcular fórmulas después de cargar los datos
      calcularFormulas()
    }
  } catch (err) {
    console.error('Error al cargar datos del prototipo:', err)
    showError('Error al cargar datos del prototipo')
  }
}

// Gestión de secciones
const addSeccion = (seccion) => {
  const newPrototipoSeccion = {
    prototipo_id: prototipoForm.value.id,
    seccion_id: seccion.id,
    orden: prototipoSecciones.value.length + 1,
    seccion: seccion
  }
  prototipoSecciones.value.push(newPrototipoSeccion)
  showAddSeccionModal.value = false
}

const removeSeccion = (index) => {
  const seccionRemovida = prototipoSecciones.value[index]
  
  // Remover componentes asociados a esta sección
  prototipoComponentes.value = prototipoComponentes.value.filter(pc => 
    pc.seccion_id !== seccionRemovida.seccion_id)
  
  prototipoSecciones.value.splice(index, 1)
  // Reordenar
  prototipoSecciones.value.forEach((ps, i) => ps.orden = i + 1)
}

const moveSeccionUp = (index) => {
  if (index > 0) {
    const temp = prototipoSecciones.value[index]
    prototipoSecciones.value[index] = prototipoSecciones.value[index - 1]
    prototipoSecciones.value[index - 1] = temp
    prototipoSecciones.value.forEach((ps, i) => ps.orden = i + 1)
  }
}

const moveSeccionDown = (index) => {
  if (index < prototipoSecciones.value.length - 1) {
    const temp = prototipoSecciones.value[index]
    prototipoSecciones.value[index] = prototipoSecciones.value[index + 1]
    prototipoSecciones.value[index + 1] = temp
    prototipoSecciones.value.forEach((ps, i) => ps.orden = i + 1)
  }
}

const isSeccionAlreadyAdded = (seccion) => {
  return prototipoSecciones.value.some(ps => ps.seccion_id === seccion.id)
}

// Gestión de componentes
const getSelectedComponenteId = (prototipoSeccion, tipoComponente) => {
  // Los datos del backend usan prototipo_seccion_id, necesitamos encontrar el componente correcto
  // También considerar el alias para distinguir entre empalme 1 y empalme 2
  let prototipoComponente = null
  
  if (isEdit.value) {
    // Modo edición: buscar por prototipo_seccion_id + alias
    prototipoComponente = prototipoComponentes.value.find(pc => 
      pc.prototipo_seccion_id === prototipoSeccion.id && 
      pc.tipo_componente_id === tipoComponente.id &&
      (pc.tipo_componente_alias === (tipoComponente.alias || null))
    )
  } else {
    // Modo creación: buscar por seccion_id + alias
    prototipoComponente = prototipoComponentes.value.find(pc => 
      pc.seccion_id === prototipoSeccion.seccion_id && 
      pc.tipo_componente_id === tipoComponente.id &&
      pc.tipo_componente_alias === (tipoComponente.alias || null)
    )
  }
  
  return prototipoComponente?.componente_id || ''
}

const updateComponenteSelection = (prototipoSeccion, tipoComponente, event) => {
  const componenteId = parseInt(event.target.value) || null
  console.log("Actualizando componente:", {
    prototipoSeccion_id: prototipoSeccion.id,
    seccion_id: prototipoSeccion.seccion_id,
    tipo_componente_id: tipoComponente.id,
    tipo_componente_alias: tipoComponente.alias,
    nuevo_componente_id: componenteId
  })
  
  // Buscar y remover selección anterior
  // En modo edición: buscar por prototipo_seccion_id Y tipo_componente_alias
  // En modo creación: buscar por seccion_id Y tipo_componente_alias
  let existingIndex = -1
  
  if (isEdit.value) {
    // Modo edición: usar prototipo_seccion_id + alias para distinguir empalmes
    existingIndex = prototipoComponentes.value.findIndex(pc => 
      pc.prototipo_seccion_id === prototipoSeccion.id && 
      pc.tipo_componente_id === tipoComponente.id &&
      (pc.tipo_componente_alias === (tipoComponente.alias || null))
    )
  } else {
    // Modo creación: usar seccion_id + alias
    existingIndex = prototipoComponentes.value.findIndex(pc => 
      pc.seccion_id === prototipoSeccion.seccion_id && 
      pc.tipo_componente_id === tipoComponente.id &&
      pc.tipo_componente_alias === (tipoComponente.alias || null)
    )
  }
  
  console.log("Componente existente encontrado en índice:", existingIndex);

  // Agregar nueva selección si hay componente seleccionado
  if (componenteId) {
    if (existingIndex >= 0) {
      // ACTUALIZAR componente existente preservando el ID
      const componenteExistente = prototipoComponentes.value[existingIndex]

      // Buscar el componente para obtener su costo unitario
      const componente = tipoComponente.componentes?.find(c => c.id === componenteId)
      const costoUnitario = parseFloat(componente?.costo_unitario || 0)
      let costoTotal = 0

      if (componente && tipoComponente.tipo_costo === 'POR_MEDIDA') {
        // Calcular por área
        const area = calcularArea(prototipoForm.value.alto, prototipoForm.value.ancho)
        costoTotal = costoUnitario * area * 1 // cantidad = 1
      } else {
        // Costo único
        costoTotal = costoUnitario * 1 // cantidad = 1
      }

      componenteExistente.componente_id = componenteId
      componenteExistente.tipo_componente_id = tipoComponente.id
      componenteExistente.tipo_componente_alias = tipoComponente.alias || null
      componenteExistente.cantidad = 1
      componenteExistente.costo_unitario = costoUnitario
      componenteExistente.costo_total = costoTotal
    } else {
      // CREAR nuevo componente solo si no existe
      // Buscar el componente para obtener su costo unitario
      const componente = tipoComponente.componentes?.find(c => c.id === componenteId)
      const costoUnitario = parseFloat(componente?.costo_unitario || 0)
      let costoTotal = 0

      if (componente && tipoComponente.tipo_costo === 'POR_MEDIDA') {
        // Calcular por área
        const area = calcularArea(prototipoForm.value.alto, prototipoForm.value.ancho)
        costoTotal = costoUnitario * area * 1 // cantidad = 1
      } else {
        // Costo único
        costoTotal = costoUnitario * 1 // cantidad = 1
      }

      const nuevoComponente = {
        componente_id: componenteId,
        tipo_componente_id: tipoComponente.id,
        tipo_componente_alias: tipoComponente.alias || null,
        cantidad: 1,
        costo_unitario: costoUnitario,
        costo_total: costoTotal
      }

      if (isEdit.value) {
        // Modo edición: usar estructura del backend
        nuevoComponente.prototipo_seccion_id = prototipoSeccion.id
      } else {
        // Modo creación: usar estructura del frontend
        nuevoComponente.prototipo_id = prototipoForm.value.id
        nuevoComponente.seccion_id = prototipoSeccion.seccion_id
        nuevoComponente.orden = prototipoComponentes.value.length + 1
      }

      prototipoComponentes.value.push(nuevoComponente)
    }
  } else if (existingIndex >= 0) {
    // Si no hay componente seleccionado pero existía uno, eliminarlo
    prototipoComponentes.value.splice(existingIndex, 1)
  }

  console.log("prototipoComponentes actualizado:", prototipoComponentes.value);

  // Recalcular fórmulas cuando cambian los componentes
  calcularFormulas()
}

const getComponenteCosto = (prototipoSeccion, tipoComponente) => {
  const componenteId = getSelectedComponenteId(prototipoSeccion, tipoComponente)
  if (!componenteId) return '0.00'
  
  // Buscar el componente en el tipo de componente
  const componente = tipoComponente.componentes?.find(c => c.id === componenteId)
  if (!componente) return '0.00'
  
  const costoUnitario = parseFloat(componente.costo_unitario || 0)
  
  // if (tipoComponente.tipo_costo === 'POR_MEDIDA') {
  //   const alto = parseFloat(prototipoForm.value.alto) || 0
  //   const ancho = parseFloat(prototipoForm.value.ancho) || 0
  //   const area = (alto * ancho) / 10000 // Convertir de cm² a m²
    
  //   if (area > 0) {
  //     return (costoUnitario * area).toFixed(2)
  //   } else {
  //     return '0.00' // Sin área = sin costo
  //   }
  // }
  
  return costoUnitario.toFixed(2)
}

// Funciones para select con búsqueda
const getSearchKey = (prototipoSeccion, tipoComponente) => {
  return `${prototipoSeccion.seccion_id}-${tipoComponente.id}-${tipoComponente.alias || 'default'}`
}


const getSelectedComponentePlaceholder = (prototipoSeccion, tipoComponente) => {
  const componenteId = getSelectedComponenteId(prototipoSeccion, tipoComponente)
  if (!componenteId) return ''
  
  if (componenteId === 0) {
    return 'Seleccionado: No Aplica'
  }
  
  const componente = tipoComponente.componentes?.find(c => c.id === componenteId)
  if (!componente) return ''
  
  const tipoCostoText = tipoComponente.tipo_costo === 'POR_MEDIDA' ? ' (x Área)' : ' (Único)'
  // Truncar el nombre si es muy largo para el placeholder
  const nombreTruncado = componente.nombre.length > 30 ? 
    `${componente.nombre.substring(0, 30)}...` : 
    componente.nombre
  return `Seleccionado: ${nombreTruncado} - $${componente.costo_unitario || 0}${tipoCostoText}`
}

const getSelectedComponenteFullName = (prototipoSeccion, tipoComponente) => {
  const componenteId = getSelectedComponenteId(prototipoSeccion, tipoComponente)
  if (!componenteId) return ''
  
  if (componenteId === 0) {
    return 'No Aplica - Sin costo adicional'
  }
  
  const componente = tipoComponente.componentes?.find(c => c.id === componenteId)
  if (!componente) return ''
  
  const tipoCostoText = tipoComponente.tipo_costo === 'POR_MEDIDA' ? ' (Por área)' : ' (Costo único)'
  return `${componente.nombre} - $${componente.costo_unitario || 0}${tipoCostoText}`
}

const openDropdown = (prototipoSeccion, tipoComponente) => {
  const key = getSearchKey(prototipoSeccion, tipoComponente)
  showDropdown.value[key] = true
  
  // Siempre inicializar con todos los componentes disponibles
  filteredComponents.value[key] = tipoComponente.componentes || []
  
  // Resetear índice resaltado
  highlightedIndex.value[key] = -1
}

const closeDropdown = (prototipoSeccion, tipoComponente) => {
  const key = getSearchKey(prototipoSeccion, tipoComponente)
  showDropdown.value[key] = false
  highlightedIndex.value[key] = -1
}

const handleInputChange = (prototipoSeccion, tipoComponente) => {
  const key = getSearchKey(prototipoSeccion, tipoComponente)
  
  // Abrir dropdown si no está abierto
  if (!showDropdown.value[key]) {
    openDropdown(prototipoSeccion, tipoComponente)
  }
  
  // Filtrar componentes
  filterComponents(prototipoSeccion, tipoComponente)
}

const filterComponents = (prototipoSeccion, tipoComponente) => {
  const key = getSearchKey(prototipoSeccion, tipoComponente)
  const searchTerm = searchTerms.value[key] || ''
  const components = tipoComponente.componentes || []
  
  if (!searchTerm.trim()) {
    filteredComponents.value[key] = components
  } else {
    filteredComponents.value[key] = components.filter(c => 
      c.nombre.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }
  
  // Resetear índice resaltado al filtrar
  highlightedIndex.value[key] = -1
}

const selectComponent = (prototipoSeccion, tipoComponente, componente) => {
  const key = getSearchKey(prototipoSeccion, tipoComponente)

  // Cerrar dropdown inmediatamente
  closeDropdown(prototipoSeccion, tipoComponente)

  // Limpiar término de búsqueda para que el placeholder muestre lo seleccionado
  searchTerms.value[key] = ''

  // Simular evento de cambio para reutilizar la lógica existente
  const mockEvent = {
    target: { value: componente.id }
  }
  updateComponenteSelection(prototipoSeccion, tipoComponente, mockEvent)
}

const selectFirstFiltered = (prototipoSeccion, tipoComponente) => {
  const key = getSearchKey(prototipoSeccion, tipoComponente)
  const filtered = filteredComponents.value[key]
  
  if (filtered && filtered.length > 0) {
    selectComponent(prototipoSeccion, tipoComponente, filtered[0])
  }
}

const selectHighlightedOrFirst = (prototipoSeccion, tipoComponente) => {
  const key = getSearchKey(prototipoSeccion, tipoComponente)
  const highlighted = highlightedIndex.value[key]
  
  if (highlighted === -1) {
    // Seleccionar "No Aplica"
    selectNoAplica(prototipoSeccion, tipoComponente)
  } else {
    const filtered = filteredComponents.value[key]
    if (filtered && filtered[highlighted]) {
      selectComponent(prototipoSeccion, tipoComponente, filtered[highlighted])
    } else {
      selectFirstFiltered(prototipoSeccion, tipoComponente)
    }
  }
}

const navigateDown = (prototipoSeccion, tipoComponente) => {
  const key = getSearchKey(prototipoSeccion, tipoComponente)
  const filtered = filteredComponents.value[key] || []
  const maxIndex = filtered.length - 1
  
  if (highlightedIndex.value[key] === undefined || highlightedIndex.value[key] === null) {
    highlightedIndex.value[key] = -1
  } else if (highlightedIndex.value[key] < maxIndex) {
    highlightedIndex.value[key]++
  }
  
  // Hacer scroll al elemento resaltado
  scrollToHighlighted(prototipoSeccion, tipoComponente)
}

const navigateUp = (prototipoSeccion, tipoComponente) => {
  const key = getSearchKey(prototipoSeccion, tipoComponente)
  
  if (highlightedIndex.value[key] === undefined || highlightedIndex.value[key] === null) {
    highlightedIndex.value[key] = -1
  } else if (highlightedIndex.value[key] > -1) {
    highlightedIndex.value[key]--
  }
  
  // Hacer scroll al elemento resaltado
  scrollToHighlighted(prototipoSeccion, tipoComponente)
}

const scrollToHighlighted = (prototipoSeccion, tipoComponente) => {
  const key = getSearchKey(prototipoSeccion, tipoComponente)
  
  // Usar nextTick para asegurar que el DOM se haya actualizado
  setTimeout(() => {
    const dropdownContainer = dropdownRefs.value[key]?.querySelector('.absolute')
    if (!dropdownContainer) return
    
    const highlightedElement = dropdownContainer.querySelector('[data-highlighted="true"]')
    if (!highlightedElement) return
    
    const containerTop = dropdownContainer.scrollTop
    const containerBottom = containerTop + dropdownContainer.clientHeight
    const elementTop = highlightedElement.offsetTop
    const elementBottom = elementTop + highlightedElement.offsetHeight
    
    // Si el elemento está por encima del área visible
    if (elementTop < containerTop) {
      dropdownContainer.scrollTop = elementTop
    }
    // Si el elemento está por debajo del área visible
    else if (elementBottom > containerBottom) {
      dropdownContainer.scrollTop = elementBottom - dropdownContainer.clientHeight
    }
  }, 10)
}

const selectNoAplica = (prototipoSeccion, tipoComponente) => {
  const key = getSearchKey(prototipoSeccion, tipoComponente)

  // Cerrar dropdown inmediatamente
  closeDropdown(prototipoSeccion, tipoComponente)

  // Limpiar término de búsqueda para que el placeholder muestre lo seleccionado
  searchTerms.value[key] = ''

  const mockEvent = { target: { value: 0 } }
  updateComponenteSelection(prototipoSeccion, tipoComponente, mockEvent)
}

const initializeSearchTermsFromSelection = () => {
  // Inicializar términos de búsqueda vacíos para que se use el placeholder
  prototipoSecciones.value.forEach(prototipoSeccion => {
    // En modo edición, necesitamos encontrar la sección completa con tipos_componente
    const seccionCompleta = seccionesDisponibles.value.find(s => s.id === prototipoSeccion.seccion_id)
    
    if (!seccionCompleta || !seccionCompleta.tipos_componente) {
      console.warn('No se encontró la sección completa para:', prototipoSeccion.seccion_id)
      return
    }
    
    // Asignar la información completa de la sección si no está presente
    if (!prototipoSeccion.seccion || !prototipoSeccion.seccion.tipos_componente) {
      prototipoSeccion.seccion = seccionCompleta
    }
    
    seccionCompleta.tipos_componente.forEach(tipoComponente => {
      const key = getSearchKey(prototipoSeccion, tipoComponente)
      // Mantener el término de búsqueda vacío para usar placeholder
      searchTerms.value[key] = ''
    })
  })
}

// Manejar clicks fuera del dropdown
const handleClickOutside = (event) => {
  // Verificar cada dropdown abierto
  Object.keys(showDropdown.value).forEach(key => {
    if (showDropdown.value[key]) {
      const dropdownElement = dropdownRefs.value[key]
      if (dropdownElement && !dropdownElement.contains(event.target)) {
        showDropdown.value[key] = false
      }
    }
  })
}

// Formateo de texto
const formatText = (command) => {
  if (descriptionTextarea.value) {
    const textarea = descriptionTextarea.value
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const selectedText = textarea.value.substring(start, end)
    
    let formattedText = selectedText
    if (command === 'bold') {
      formattedText = `**${selectedText}**`
    } else if (command === 'italic') {
      formattedText = `*${selectedText}*`
    }
    
    const newValue = textarea.value.substring(0, start) + formattedText + textarea.value.substring(end)
    prototipoForm.value.descripcion = newValue
    
    // Restaurar el foco
    setTimeout(() => {
      textarea.focus()
      textarea.setSelectionRange(start, start + formattedText.length)
    }, 0)
  }
}

// Función auxiliar para obtener componentes de una sección específica
const getComponentesForSeccion = (prototipoSeccion) => {
  const componentes = []
  
  // Buscar componentes que pertenecen a esta sección
  prototipoComponentes.value.forEach(pc => {
    let belongsToSection = false
    
    // En modo edición: verificar por prototipo_seccion_id
    if (isEdit.value && prototipoSeccion.id && pc.prototipo_seccion_id === prototipoSeccion.id) {
      belongsToSection = true
    }
    // En modo edición para secciones nuevas: verificar por seccion_id (no tienen prototipo_seccion_id aún)
    else if (isEdit.value && !pc.prototipo_seccion_id && pc.seccion_id === prototipoSeccion.seccion_id) {
      belongsToSection = true
    }
    // En modo creación: verificar por seccion_id
    else if (!isEdit.value && pc.seccion_id === prototipoSeccion.seccion_id) {
      belongsToSection = true
    }
    
    if (belongsToSection && pc.componente_id && pc.componente_id !== 0) {
      // Buscar el tipo de componente y el componente para obtener el costo
      let tipoComponente = null
      let componente = null

      // Buscar en todas las secciones para encontrar el tipo de componente
      for (const seccion of seccionesDisponibles.value) {
        const tc = seccion.tipos_componente?.find(tc => tc.id === pc.tipo_componente_id)
        if (tc) {
          tipoComponente = tc
          componente = tc.componentes?.find(c => c.id === pc.componente_id)
          break
        }
      }

      let costoUnitario = 0
      let costoTotal = 0

      if (componente && tipoComponente) {
        costoUnitario = parseFloat(componente.costo_unitario || 0)
        const cantidad = pc.cantidad || 1

        if (tipoComponente.tipo_costo === 'POR_MEDIDA') {
          // Calcular por área
          const alto = parseFloat(prototipoForm.value.alto) || 0
          const ancho = parseFloat(prototipoForm.value.ancho) || 0
          const area = (alto * ancho) / 10000 // Convertir cm² a m²
          costoTotal = costoUnitario * area * cantidad
        } else {
          // Costo único
          costoTotal = costoUnitario * cantidad
        }
      }

      componentes.push({
        id: pc.id || null, // ID de prototipo_componente (null si es nuevo)
        tipo_componente_id: pc.tipo_componente_id,
        componente_id: pc.componente_id,
        tipo_componente_alias: pc.tipo_componente_alias || null,
        cantidad: pc.cantidad || 1,
        costo_unitario: costoUnitario,
        costo_total: costoTotal
      })
    }
  })
  
  return componentes
}

// Acciones principales
const saveDraft = async () => {
  await savePrototipo(true)
}

const publishPrototipo = async () => {
  await savePrototipo(false)
}

const savePrototipo = async (isDraft = false) => {
  try {
    validationErrors.value = {}
    console.log("Estoy en savePrototipo isDraft = false");
    // Validación: componentes POR_MEDIDA requieren dimensiones
    if (!isDraft && tieneComponentesPorMedidaSinArea.value) {
      console.log("Estoy dentro de Validación");
      validationErrors.value.alto = ['Las dimensiones son requeridas para componentes que se cobran por área.']
      validationErrors.value.ancho = ['Las dimensiones son requeridas para componentes que se cobran por área.']
      
      await Swal.fire({
        title: 'Dimensiones requeridas',
        text: 'Ingresa las dimensiones (alto y ancho) para calcular correctamente el costo de los componentes que se cobran por área.',
        icon: 'warning',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#8B5CF6',
        timer: 0,
        timerProgressBar: false
      })
      return
    }
    else{
      console.log(" !isDraft && tieneComponentesPorMedidaSinArea.value: False ");
    }
    

    // Preparar payload para el endpoint inteligente
    const payload = {
      nombre: prototipoForm.value.nombre,
      descripcion: prototipoForm.value.descripcion,
      alto: parseFloat(prototipoForm.value.alto) || 0,
      ancho: parseFloat(prototipoForm.value.ancho) || 0,
      profundidad: parseFloat(prototipoForm.value.profundidad) || 0,
      costo_base: costoBase.value,
      estado: isDraft ? 'borrador' : 'aprobado',
      notas: prototipoForm.value.descripcion,
      // Nuevos campos agregados
      proyecto: prototipoForm.value.proyecto,
      fraccion: prototipoForm.value.fraccion,
      cliente: prototipoForm.value.cliente,
      poses: parseInt(prototipoForm.value.poses) || 1,
      pago_herramental: prototipoForm.value.pago_herramental,
      valor_pago_herramental: parseFloat(prototipoForm.value.valor_pago_herramental) || 0,
      costo_calculado: parseFloat(prototipoForm.value.costo_calculado) || 0,
      precio_unitario: preciosCalculados.value.precio1,
      secciones: prototipoSecciones.value.map(ps => ({
        id: ps.id || null, // ID de prototipo_seccion (null si es nueva)
        seccion_id: ps.seccion_id,
        orden: ps.orden,
        componentes: getComponentesForSeccion(ps)
      })),
      // Incluir porcentajes del negocio seleccionado
      porcentajes_negocio: userStore.negocioSeleccionado ? {
        negocio_id: userStore.negocioSeleccionado.id,
        porcentaje1: userStore.negocioSeleccionado.porcentaje1 || 25.00,
        porcentaje2: userStore.negocioSeleccionado.porcentaje2 || 35.00,
        porcentaje3: userStore.negocioSeleccionado.porcentaje3 || 50.00,
        porcentaje4: userStore.negocioSeleccionado.porcentaje4 || 75.00
      } : null
    }

    console.log("payload: ", JSON.stringify(payload, null, 2));

    if (isEdit.value) {
      // Usar el nuevo endpoint inteligente
      console.log('Enviando actualización...')
      await post(`prototipos/${prototipoId.value}/smart-update?_method=PUT`, payload)
      console.log('Actualización exitosa')
      showSuccess('Prototipo actualizado exitosamente')
    } else {
      console.log('Enviando creación...')
      await post('prototipos', payload)
      console.log('Creación exitosa')
      showSuccess(`Prototipo ${isDraft ? 'guardado como borrador' : 'publicado'} exitosamente`)
    }

    goBack()
  } catch (err) {
    console.error('Error completo:', err)
    console.error('Error value:', error.value)
    console.error('Error response:', err.response)
    console.error('Error response data:', err.response?.data)
    console.error('Error response errors:', err.response?.data?.errors)

    if (error.value && typeof error.value === 'object' && error.value.errors) {
      validationErrors.value = error.value.errors
      console.error('Errores de validación:', validationErrors.value)
    }
    showError(error.value?.message || 'Error al guardar prototipo')
  }
}


const goBack = () => {
  router.push('/prototipos')
}

// Watchers para recalcular fórmulas automáticamente
watch(() => prototipoForm.value.pago_herramental, () => {
  console.log('Cambió pago_herramental, recalculando fórmulas...')
  calcularFormulas()
})

// Removed: cantidad watcher - cantidad es para cotizaciones, no prototipos

watch(() => prototipoForm.value.poses, () => {
  console.log('Cambió poses, recalculando fórmulas...')
  calcularFormulas()
})

onMounted(() => {
  loadInitialData()
  // Agregar event listener para clicks fuera
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  // Remover event listener al desmontar el componente
  document.removeEventListener('click', handleClickOutside)
})
</script>